"use client";

import type { ReactNode } from "react";
import { AGENT_CATALOG } from "~/lib/siteglue/business-ready/agent-catalog";
import { CHANNEL_CATALOG } from "~/lib/siteglue/business-ready/channel-catalog";
import type { AgentId, ChannelId } from "~/lib/siteglue/business-ready/types";
import {
  isAeoSelected,
  isAgentTestSelected,
  isAroSelected,
  isCroSelected,
  type ScanReportKind,
} from "~/lib/siteglue/agent-readiness/selection";
import { FEATURED_WALK_AGENTS } from "~/lib/siteglue/agent-ready/walk-agents";
import {
  SCAN_AGENTS_LABEL,
  SCAN_CHANNELS_LABEL,
  SCAN_REPORT_AGENTS_TEST_HELP,
  SCAN_REPORT_AGENTS_TEST_TITLE,
  SCAN_REPORT_AEO_HELP,
  SCAN_REPORT_AEO_TITLE,
  SCAN_REPORT_ARO_HELP,
  SCAN_REPORT_ARO_TITLE,
  SCAN_REPORT_CRO_HELP,
  SCAN_REPORT_CRO_TITLE,
  SCAN_WALK_TASKS_LABEL,
  SCAN_WIZARD_AGENTS_HELP,
  SCAN_WIZARD_CHANNELS_HELP,
} from "~/lib/siteglue/landing-redesign/copy";
import { cn } from "~/lib/utils";

const CRO_SETUP_CHANNEL_IDS: ChannelId[] = ["website", "browser_call", "phone", "sms", "email"];
const CRO_SETUP_CHANNELS = CHANNEL_CATALOG.filter((channel) => CRO_SETUP_CHANNEL_IDS.includes(channel.id));

export const DEFAULT_AUDIT_REPORTS: ScanReportKind[] = ["aro", "cro"];

export const WALK_TASK_OPTIONS = [
  { id: "what_you_do", label: "What you do" },
  { id: "get_answers", label: "Get answers" },
  { id: "contact", label: "Contact" },
  { id: "book", label: "Book" },
  { id: "after_hours", label: "After hours" },
  { id: "agent_act", label: "Take action" },
] as const;

export type WalkTaskId = (typeof WALK_TASK_OPTIONS)[number]["id"];

function toggleReport(current: ScanReportKind[], id: ScanReportKind): ScanReportKind[] {
  return current.includes(id) ? current.filter((item) => item !== id) : [...current, id];
}

function toggleValue<T extends string>(current: T[], id: T, locked?: T): T[] {
  if (locked && id === locked) return current.includes(id) ? current : [...current, id];
  return current.includes(id) ? current.filter((item) => item !== id) : [...current, id];
}

function AuditCard({
  title,
  help,
  checked,
  onToggle,
  children,
}: {
  title: string;
  help: string;
  checked: boolean;
  onToggle: () => void;
  children?: ReactNode;
}) {
  return (
    <div
      className={cn(
        "rounded-2xl border bg-white",
        checked ? "border-[#4f81bd] bg-[#4f81bd]/[0.06]" : "border-slate-200",
      )}
    >
      <label className="flex cursor-pointer items-start gap-3 p-4">
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          className="mt-1 h-4 w-4 rounded border-slate-300 text-[#4f81bd] focus:ring-[#4f81bd]"
        />
        <span>
          <span className="block text-sm font-semibold text-slate-900">{title}</span>
          <span className="mt-1 block text-sm leading-relaxed text-slate-600">{help}</span>
        </span>
      </label>
      {checked && children ? <div className="border-t border-[#4f81bd]/15 px-4 pb-4 pt-3">{children}</div> : null}
    </div>
  );
}

export function ScanAuditCards({
  reports,
  onReportsChange,
  agents,
  onAgentsChange,
  channels,
  onChannelsChange,
  walkTasks,
  onWalkTasksChange,
}: {
  reports: ScanReportKind[];
  onReportsChange: (value: ScanReportKind[]) => void;
  agents: AgentId[];
  onAgentsChange: (value: AgentId[]) => void;
  channels: ChannelId[];
  onChannelsChange: (value: ChannelId[]) => void;
  walkTasks: string[];
  onWalkTasksChange: (value: string[]) => void;
}) {
  const aro = isAroSelected(reports);
  const cro = isCroSelected(reports);
  const aeo = isAeoSelected(reports);
  const agentTest = isAgentTestSelected(reports);

  return (
    <div className="space-y-2.5">
      <AuditCard
        title={SCAN_REPORT_ARO_TITLE}
        help={SCAN_REPORT_ARO_HELP}
        checked={aro}
        onToggle={() => onReportsChange(toggleReport(reports, "aro"))}
      />
      <AuditCard
        title={SCAN_REPORT_AEO_TITLE}
        help={SCAN_REPORT_AEO_HELP}
        checked={aeo}
        onToggle={() => onReportsChange(toggleReport(reports, "aeo"))}
      />
      <AuditCard
        title={SCAN_REPORT_CRO_TITLE}
        help={SCAN_REPORT_CRO_HELP}
        checked={cro}
        onToggle={() =>
          onReportsChange(toggleReport(reports.filter((item) => item !== "hro" && item !== "channels"), "cro"))
        }
      >
        <p className="text-sm font-semibold text-slate-900">{SCAN_CHANNELS_LABEL}</p>
        <p className="mt-1 text-sm text-slate-600">{SCAN_WIZARD_CHANNELS_HELP}</p>
        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-2">
          <label className="inline-flex cursor-default items-center gap-2 text-sm text-slate-500">
            <input type="checkbox" checked disabled className="h-4 w-4 rounded border-slate-300 text-[#4f81bd]" />
            Website Forms
          </label>
          {CRO_SETUP_CHANNELS.map((channel) => (
            <label
              key={channel.id}
              className={cn(
                "inline-flex items-center gap-2 text-sm text-slate-800",
                channel.id === "website" ? "cursor-default text-slate-500" : "cursor-pointer",
              )}
            >
              <input
                type="checkbox"
                checked={channels.includes(channel.id)}
                disabled={channel.id === "website"}
                onChange={() => onChannelsChange(toggleValue(channels, channel.id, "website"))}
                className="h-4 w-4 rounded border-slate-300 text-[#4f81bd] focus:ring-[#4f81bd]"
              />
              {channel.id === "website" ? "AI Live Chat" : channel.label}
            </label>
          ))}
        </div>
      </AuditCard>
      <AuditCard
        title={SCAN_REPORT_AGENTS_TEST_TITLE}
        help={SCAN_REPORT_AGENTS_TEST_HELP}
        checked={agentTest}
        onToggle={() => {
          const next = toggleReport(reports, "agents-test");
          onReportsChange(next);
          if (isAgentTestSelected(next) && agents.length === 0) {
            onAgentsChange([...FEATURED_WALK_AGENTS]);
          }
          if (isAgentTestSelected(next) && walkTasks.length === 0) {
            onWalkTasksChange(WALK_TASK_OPTIONS.map((item) => item.id));
          }
        }}
      >
        <p className="text-sm font-semibold text-slate-900">{SCAN_AGENTS_LABEL}</p>
        <p className="mt-1 text-sm text-slate-600">{SCAN_WIZARD_AGENTS_HELP}</p>
        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-2">
          {AGENT_CATALOG.map((agent) => (
            <label key={agent.id} className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-800">
              <input
                type="checkbox"
                checked={agents.includes(agent.id)}
                onChange={() => onAgentsChange(toggleValue(agents, agent.id))}
                className="h-4 w-4 rounded border-slate-300 text-[#4f81bd] focus:ring-[#4f81bd]"
              />
              {agent.label}
            </label>
          ))}
        </div>
        <p className="mt-4 text-sm font-semibold text-slate-900">{SCAN_WALK_TASKS_LABEL}</p>
        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-2">
          {WALK_TASK_OPTIONS.map((task) => (
            <label key={task.id} className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-800">
              <input
                type="checkbox"
                checked={walkTasks.includes(task.id)}
                onChange={() => onWalkTasksChange(toggleValue(walkTasks, task.id))}
                className="h-4 w-4 rounded border-slate-300 text-[#4f81bd] focus:ring-[#4f81bd]"
              />
              {task.label}
            </label>
          ))}
        </div>
      </AuditCard>
    </div>
  );
}
